"use client";
import { Slot } from "@radix-ui/react-slot";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "./variants";

// ----------------------------------------------------------------------

export interface FloatingLabelButtonProps
  extends Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      "color" | "value"
    >,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  ring?: boolean;
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error";
  size?: "default" | "sm" | "md" | "lg" | "icon";
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;

  label: string;
  value: React.ReactNode;
  error?: boolean;
  fieldsetClassName?: string;
}

const FloatingLabelButton = React.forwardRef<
  HTMLButtonElement,
  FloatingLabelButtonProps
>(
  (
    {
      className,
      color,
      size,
      asChild = false,
      error = false,
      startIcon,
      endIcon,
      label,
      value,
      fieldsetClassName,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        data-start-icon={!!startIcon}
        data-end-icon={!!endIcon}
        style={{
          minWidth: "calc(1.5rem + 80px)",
        }}
        className={cn(
          buttonVariants({ color, size, className }),
          "justify-start relative group border-0 h-12",
          {
            "text-error": !!error,
          },
        )}
        type="button"
        ref={ref}
        {...props}
        data-value={!!value}
      >
        {!!startIcon && <span className="shrink-0 me-1">{startIcon}</span>}
        <span
          className={cn(
            "absolute text-primary transition-all group-data-[value=true]:-translate-y-4 group-data-[value=true]:top-2 group-data-[value=true]:text-xs grow shrink-0",
            {
              "group-data-[value=true]:-translate-x-1 translate-x-7":
                !!startIcon,
            },
          )}
        >
          {label}
        </span>
        {value ? (
          <span className="text-start">{value}</span>
        ) : (
          <span className="w-full" />
        )}
        {!!endIcon && <span className="shrink-0 ms-auto">{endIcon}</span>}
        <fieldset
          className={cn(
            "absolute group-focus:border-2 group-focus-visible:border-2 transition-all group-focus:border-primary group-focus-visible:border-primary inset-0 -top-[5px] border border-stroke-light rounded-xs m-0 py-0 text-start ps-2 pe-2 pointer-events-none min-w-0 group-focus:[&>legend]:max-w-full group-focus-visible:[&>legend]:max-w-full group-data-[value=false]:[&>legend]:max-w-0",
            {
              "text-error border-error": !!error,
            },
            fieldsetClassName,
          )}
        >
          <legend className="transition-all invisible whitespace-nowrap overflow-hidden w-auto max-w-full h-3 leading-4 text-xs font-normal p-0">
            <span className="ps-1 pe-1 visible inline-block opacity-0">
              {label}
            </span>
          </legend>
        </fieldset>
      </Comp>
    );
  },
);

FloatingLabelButton.displayName = "FloatingLabelButton";

export { FloatingLabelButton };
